import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FaSearch, FaBell, FaSun, FaMoon } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';

const Header = ({ forceHide = false, userName = 'User', onNotificationClick, notificationCount = 0 }) => {
  const [promptEvent, setPromptEvent] = useState(null);
  const [appAccepted, setAppAccepted] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const { theme, toggleTheme, isDark } = useTheme();

  let isAppInstalled = false;

  if (window.matchMedia('(display-mode: standalone)').matches || appAccepted) {
    isAppInstalled = true;
  }

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setPromptEvent(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const installApp = () => {
    if (promptEvent) {
      promptEvent.prompt();
      promptEvent.userChoice.then((result) => {
        if (result.outcome === 'accepted') {
          setAppAccepted(true);
          console.log('User accepted the A2HS prompt');
        } else {
          console.log('User dismissed the A2HS prompt');
        }
      });
    }
  };

  // Don't render if forced to hide
  if (forceHide) {
    return null;
  }

  return (
    <header className={`sticky top-0 z-30 transition-all duration-300 ${isDark
      ? 'bg-dark-secondary/80 border-b border-white/5'
      : 'bg-white/80 border-b border-gray-200'
      } backdrop-blur-xl`}>
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-3 sm:py-4 gap-4">

          {/* Search Bar - Left/Center */}
          <div className="flex-1 max-w-xl">
            <motion.div
              className={`relative transition-all duration-300 ${isSearchFocused ? 'scale-[1.02]' : ''
                }`}
            >
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <FaSearch className={`text-sm transition-colors ${isSearchFocused
                  ? 'text-purple-500'
                  : isDark ? 'text-gray-500' : 'text-gray-400'
                  }`} />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                placeholder="Search questions, topics, tests..."
                className={`w-full pl-10 pr-4 py-2.5 rounded-xl text-sm transition-all duration-300 outline-none ${isDark
                  ? 'bg-dark-tertiary/50 border border-white/10 text-white placeholder-gray-500 focus:border-purple-500/50 focus:bg-dark-tertiary'
                  : 'bg-gray-100 border border-transparent text-gray-900 placeholder-gray-500 focus:border-purple-500/50 focus:bg-white focus:shadow-lg'
                  }`}
              />
              {searchQuery && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center"
                  onClick={() => setSearchQuery('')}
                >
                  <span className={`text-xs px-2 py-1 rounded-md ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'
                    }`}>
                    ESC
                  </span>
                </motion.button>
              )}
            </motion.div>
          </div>

          {/* Right side actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Notification Bell */}
            <motion.button
              onClick={onNotificationClick}
              className={`relative p-2.5 rounded-xl transition-all ${isDark
                ? 'bg-dark-tertiary/50 text-gray-300 hover:bg-dark-tertiary hover:text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Notifications"
            >
              <FaBell className="text-sm" />
              {notificationCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {notificationCount > 9 ? '9+' : notificationCount}
                </span>
              )}
            </motion.button>

            {/* Theme Toggle */}
            <motion.button
              onClick={toggleTheme}
              className={`p-2.5 rounded-xl transition-all ${isDark
                ? 'bg-dark-tertiary/50 text-yellow-400 hover:bg-dark-tertiary'
                : 'bg-gray-100 text-orange-500 hover:bg-gray-200'
                }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Toggle theme"
            >
              <AnimatePresence mode="wait">
                {isDark ? (
                  <motion.div
                    key="moon"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FaMoon className="text-sm" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="sun"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FaSun className="text-sm" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>

            {/* Install App Button */}
            {promptEvent && !isAppInstalled && (
              <motion.button
                className="hidden sm:flex px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm rounded-xl font-medium transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/25"
                onClick={installApp}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Install App
              </motion.button>
            )}

            {/* User Profile Avatar */}
            <motion.div
              className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center cursor-pointer overflow-hidden ${isDark
                ? 'bg-gradient-to-br from-purple-600 to-pink-600'
                : 'bg-gradient-to-br from-purple-500 to-indigo-600'
                }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="text-white font-semibold text-sm">
                {userName.charAt(0).toUpperCase()}
              </span>
            </motion.div>
          </div>
        </div>
      </div>
    </header>
  );
};

Header.propTypes = {
  forceHide: PropTypes.bool,
  userName: PropTypes.string,
  onNotificationClick: PropTypes.func,
  notificationCount: PropTypes.number,
};

export default Header;